import React, { useMemo } from 'react';
import cx from 'clsx';

import styles from '@danske/sapphire-css/components/skeleton/skeleton.module.css';

import { useSapphireStyleProps, useThemeCheck } from '../../utils';
import { SkeletonProps } from './skeleton.model';
import { getAnimationDelay } from './animation';

interface SkeletonTextFixedWidth {
  /**
   * Width type. Will be used to determine width calculation.
   * @default 'randomPct'
   * */
  widthType?: 'fixed';
  /** Allowed width of a skeleton */
  width: number;
  minWidth?: never;
  maxWidth?: never;
}

interface SkeletonTextRandomPxWidth {
  /**
   * Width type. Will be used to determine width calculation.
   * @default 'randomPct'
   */
  widthType?: 'randomPx';
  /** Min width of skeleton. Used internally to calculate width of a component */
  minWidth?: number;
  /** Max width of skeleton. Used internally to calculate width of a component */
  maxWidth?: number;
}

interface SkeletonTextRandomPcWidth {
  /**
   * Width type. Will be used to determine width calculation.
   * @default 'randomPct'
   */
  widthType?: 'randomPct';
  /** Min width of skeleton. Used internally to calculate width of a component */
  minWidth?: number;
  /** Max width of skeleton. Used internally to calculate width of a component */
  maxWidth?: number;
}

export type SkeletonTextProps = SkeletonProps &
  (
    | SkeletonTextFixedWidth
    | SkeletonTextRandomPxWidth
    | SkeletonTextRandomPcWidth
  );

const isFixedWidthType = (
  props: SkeletonTextProps
): props is SkeletonProps & SkeletonTextFixedWidth => {
  return props.widthType === 'fixed';
};

export const SkeletonText: React.FC<SkeletonTextProps> = (props) => {
  useThemeCheck();

  const {
    index,
    widthType = 'randomPct',
    UNSAFE_className,
    UNSAFE_style,
    children,
    minWidth = 50,
    maxWidth = 100,
    ...rest
  } = props;

  const animationDelay = getAnimationDelay(index);
  // Excluding minWidth & maxWidth props here as they're used only to calculate relevant width but not to define constraints.
  const { styleProps } = useSapphireStyleProps({
    UNSAFE_className,
    UNSAFE_style,
  });

  const skeletonWidth = useMemo(() => {
    if (isFixedWidthType(props)) {
      return props.width;
    }

    const calculatedWidth = Math.random() * (maxWidth - minWidth) + minWidth;

    return widthType === 'randomPct' ? `${calculatedWidth}%` : calculatedWidth;
  }, []);

  return (
    <span
      {...rest}
      style={{
        width: skeletonWidth,
        animationDelay,
        ...styleProps.style,
      }}
      className={cx(
        styles['sapphire-skeleton'],
        styles['sapphire-skeleton--text'],
        styleProps.className
      )}
    />
  );
};
